import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

export default function LandscapeSlideshow({ slides, label, lang, className = '', interval = 9000, eager = false }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [requested, setRequested] = useState(() => new Set([slides[0]]));
  const [loaded, setLoaded] = useState(() => new Set());
  const [displayed, setDisplayed] = useState(0);
  const root = useRef(null);
  const count = slides.length;
  const it = lang === 'it';

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(preference.matches);
    const updatePage = () => setPageVisible(!document.hidden);
    updateMotion(); updatePage();
    preference.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updatePage);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.15 });
    if (root.current) observer.observe(root.current);
    return () => {
      observer.disconnect();
      preference.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updatePage);
    };
  }, []);

  // Request the selected photo only when needed; retain previous photos for transitions.
  useEffect(() => {
    const src = slides[index];
    setRequested(current => current.has(src) ? current : new Set([...current, src]));
    if (loaded.has(src)) setDisplayed(index);
  }, [index, slides, loaded]);

  // Warm the next slide after the first photo is visible, without competing at startup.
  useEffect(() => {
    if (!visible || !pageVisible || paused || interacting || reducedMotion || count < 2 || !loaded.has(slides[index])) return;
    const timer = window.setTimeout(() => {
      const src = slides[(index + 1) % count];
      setRequested(current => current.has(src) ? current : new Set([...current, src]));
    }, Math.min(2500, interval / 2));
    return () => window.clearTimeout(timer);
  }, [visible, pageVisible, paused, interacting, reducedMotion, count, loaded, slides, index, interval]);

  useEffect(() => {
    if (paused || interacting || !visible || !pageVisible || reducedMotion || count < 2 || !loaded.has(slides[index])) return;
    const timer = window.setInterval(() => setIndex(current => (current + 1) % count), interval);
    return () => window.clearInterval(timer);
  }, [paused, interacting, visible, pageVisible, reducedMotion, count, interval, loaded, slides, index]);

  const move = delta => { setPaused(true); setIndex(current => (current + delta + count) % count); };
  const controlClass = 'p-1.5 rounded-full hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white';
  return <div ref={root} className={`pa-landscape-slideshow ${className}`} role="region" aria-roledescription={it ? 'Presentazione di immagini' : 'Image slideshow'} aria-label={label}
    onMouseEnter={() => setInteracting(true)} onMouseLeave={() => setInteracting(false)}
    onFocusCapture={() => setInteracting(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setInteracting(false); }}>
    {slides.map((src, slideIndex) => requested.has(src) ? <img key={src} src={src} decoding="async" onLoad={() => setLoaded(current => current.has(src) ? current : new Set([...current, src]))} alt="" aria-hidden="true" loading={eager && slideIndex === 0 ? 'eager' : 'lazy'} fetchPriority={eager && slideIndex === 0 ? 'high' : 'auto'}
      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 motion-reduce:transition-none ${slideIndex === displayed ? 'opacity-100' : 'opacity-0'}`} /> : null)}
    <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1 bg-[#293D2C]/85 text-white rounded-full px-1.5 py-1">
      <button type="button" className={controlClass} onClick={() => move(-1)} aria-label={it ? 'Immagine precedente' : 'Previous image'}><ChevronLeft size={16} /></button>
      <span className="text-xs tabular-nums px-1" aria-label={it ? `Immagine ${index + 1} di ${count}` : `Image ${index + 1} of ${count}`}>{index + 1}/{count}</span>
      {!reducedMotion && <button type="button" className={controlClass} onClick={() => setPaused(current => !current)} aria-label={paused ? (it ? 'Avvia presentazione' : 'Play slideshow') : (it ? 'Metti in pausa' : 'Pause slideshow')}>{paused ? <Play size={14} /> : <Pause size={14} />}</button>}
      <button type="button" className={controlClass} onClick={() => move(1)} aria-label={it ? 'Immagine successiva' : 'Next image'}><ChevronRight size={16} /></button>
    </div>
  </div>;
}
